"use client";

import React from "react";
import { X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TooltipWithContent } from "@/components/ui/tooltip";

interface ContextualTableFilterProps {
  /** Label to display (e.g., "Filtered by: Contact: John Doe") */
  label: string | null;

  /** Whether the context filter is currently active */
  isActive: boolean;

  /** Callback when user clicks clear button */
  onClear: () => void;

  /** Optional CSS class */
  className?: string;
}

/**
 * ContextualTableFilter - Displays active context filter as a badge with clear button
 *
 * Shows a visual indicator that the table is pre-filtered by parent entity context
 * Allows users to remove the context filter if desired
 *
 * Usage:
 * ```tsx
 * const { contextFilterLabel, isContextFilterActive, clearContextFilter } = useContextualTableFilter("tickets");
 *
 * return (
 *   <ContextualTableFilter
 *     label={contextFilterLabel}
 *     isActive={isContextFilterActive}
 *     onClear={clearContextFilter}
 *   />
 * );
 * ```
 */
export function ContextualTableFilter({
  label,
  isActive,
  onClear,
  className = "",
}: ContextualTableFilterProps) {
  if (!label || !isActive) {
    return null;
  }

  return (
    <div className={`flex items-center gap-2 mb-4 ${className}`}>
      <TooltipWithContent content="This table is filtered by the parent entity. Click the X to remove this filter.">
        <Badge variant="secondary" className="flex items-center gap-2 px-3 py-1.5">
          <span className="text-sm">{label}</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-4 w-4 p-0 hover:bg-transparent"
            onClick={onClear}
            aria-label="Clear context filter"
          >
            <X className="h-3 w-3" />
          </Button>
        </Badge>
      </TooltipWithContent>
    </div>
  );
}
